import React, { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCcw, X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { reportClientEvent } from "@/lib/telemetry";

// Sichere Versionspruefung:
// - erkennt eine neue App-Version (per /api/meta) oder einen wartenden Service Worker
// - zeigt einen dezenten Hinweis "Neue Version verfuegbar" mit Button "Jetzt aktualisieren"
// - aktualisiert NIE automatisch (keine verlorenen Formulare/Eingaben) — nur auf Klick
// - entfernt beim Update kontrolliert alte Caches und aktiviert den neuen Service Worker
export default function VersionGate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const initialVersion = useRef(null);
  const newVersion = useRef(null);
  const waitingSW = useRef(null);

  const checkVersion = useCallback(async () => {
    try {
      const { data } = await api.get("/meta", { params: { _t: Date.now() } });
      const v = data?.version;
      if (!v) return;
      if (initialVersion.current == null) {
        initialVersion.current = v;
        return;
      }
      if (v !== initialVersion.current) {
        newVersion.current = v;
        setUpdateAvailable(true);
      }
    } catch (e) {
      /* offline / kurzzeitig — ignorieren */
    }
  }, []);

  useEffect(() => {
    checkVersion();
    const iv = setInterval(checkVersion, 60000);
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);

    let reg;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistration()
        .then(async (r) => {
          if (!r) return;
          reg = r;
          if (r.waiting) {
            waitingSW.current = r.waiting;
            setUpdateAvailable(true);
          }
          r.addEventListener("updatefound", () => {
            const nw = r.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                waitingSW.current = r.waiting || nw;
                setUpdateAvailable(true);
              }
            });
          });
          try {
            // update() kann nach einem Dev-Server-Neustart asynchron scheitern.
            if (r.update) await r.update();
          } catch (e) {}
        })
        .catch(() => {});
    }

    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [checkVersion]);

  const doUpdate = async () => {
    if (busy) return;
    setBusy(true);
    reportClientEvent({
      type: "version_update",
      message: `Aktualisierung ${initialVersion.current || "?"} -> ${newVersion.current || "neu"}`,
      app_version: newVersion.current || undefined,
    });
    try {
      if (waitingSW.current) waitingSW.current.postMessage("SKIP_WAITING");
    } catch (e) {}
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) {}
    // kurze Verzoegerung, damit Telemetrie/SW durchlaufen — danach harter Neuladen
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      data-testid="version-update-banner"
      className="fixed left-1/2 z-[9998] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 bottom-24 lg:bottom-4"
    >
      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#14161C]/95 p-3 pr-2 shadow-2xl backdrop-blur">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(163,230,53,0.12)" }}>
          <Sparkles className="h-4.5 w-4.5" style={{ color: "#A3E635" }} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight text-white">Neue Version verfügbar</div>
          <div className="truncate text-[11px] text-gray-400">Jetzt aktualisieren für die neuste Version.</div>
        </div>
        <button
          data-testid="version-update-btn"
          onClick={doUpdate}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold text-black transition-transform hover:scale-[1.03] disabled:opacity-60"
          style={{ background: "#A3E635" }}
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Wird geladen…" : "Jetzt aktualisieren"}
        </button>
        <button
          data-testid="version-update-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Hinweis schliessen"
          className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-white/5 hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
